/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingModal from '../modals/LoadingModal';
import {
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useGNMAReportingContext } from '../../contexts/GNMAReportingContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { NumericFormat } from 'react-number-format';
import type {
  SCMORecord,
  SCMNRecord,
  EditableLoanFields,
  EditablePoolFields,
  PendingLoanEdits,
  PendingPoolEdits,
} from '../../types/GNMAReportingTypes';
import Styles from '../../css/GNMAReportingForm.module.css';

type GNMAReportingFormProps = {
  selectedTable: string;
};

enum Tables {
  POOLS = 'Pools',
  LOANS = 'Loans',
}

type GNMAPoolRowProps = {
  record: SCMNRecord;
  updateField: (id: string, field: keyof EditablePoolFields, value: number) => void;
  pendingEdits?: Partial<SCMNRecord>;
};

type GNMALoanRowProps = {
  record: SCMORecord;
  updateField: (id: string, field: keyof EditableLoanFields, value: number | string) => void;
  pendingEdits?: Partial<SCMORecord>;
};

const GNMAPoolRow = memo(({ record, updateField, pendingEdits }: GNMAPoolRowProps) => {
  return (
    <Fragment>
      <TableCell align='center'>
        <TextField
          label='Reporting Date'
          value={record.reportingDate ? dayjs.unix(record.reportingDate).format('MM/DD/YYYY') : ''}
          disabled
        />
      </TableCell>
      <TableCell align='center'>
        <TextField label='Pool Number' value={record.poolNumber} disabled />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Adjust FIC'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.adjustPi ?? record.adjustPi}
          customInput={TextField}
          allowNegative={true}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'adjustPi', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Pool FIC'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.poolPi ?? record.poolPi}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'poolPi', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Servicing Fee'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.servFee ?? record.servFee}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'servFee', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='WAIR'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.wghtAvgIntRate ?? record.wghtAvgIntRate}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={4}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'wghtAvgIntRate', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Net Adj RPB'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.adjustRpb ?? record.adjustRpb}
          customInput={TextField}
          allowNegative={true}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'adjustRpb', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Def GPM Int'
          inputProps={{ inputMode: 'numeric' }}
          value={0.0}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          disabled
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Serial Note'
          inputProps={{ inputMode: 'numeric' }}
          value={0.0}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          disabled
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Security RPB'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.securityRpb ?? record.securityRpb}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'securityRpb', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='T&I Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.poolEscrowBal ?? record.poolEscrowBal}
          customInput={TextField}
          allowNegative={true}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'poolEscrowBal', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='P&I Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.fundBal ?? record.fundBal}
          customInput={TextField}
          allowNegative={true}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'fundBal', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Other Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={0.0}
          customInput={TextField}
          allowNegative={true}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          disabled
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='RR Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.replaceReserveBal ?? record.replaceReserveBal}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.poolNumber, 'replaceReserveBal', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Const Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={0.0}
          customInput={TextField}
          allowNegative={true}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          disabled
        />
      </TableCell>
    </Fragment>
  );
});

const GNMALoanRow = memo(({ record, updateField, pendingEdits }: GNMALoanRowProps) => {
  return (
    <Fragment>
      <TableCell align='center'>
        <TextField
          label='Reporting Date'
          value={record.reportDate ? dayjs.unix(record.reportDate).format('MM/DD/YYYY') : ''}
          disabled
        />
      </TableCell>
      <TableCell align='center'>
        <TextField label='Pool Number' value={record.investorPool} disabled />
      </TableCell>
      <TableCell align='center'>
        <TextField label='Servicer Loan Number' value={record.servicerLoanNo} disabled />
      </TableCell>
      <TableCell align='center'>
        <TextField
          label='GNMA Loan Number'
          value={pendingEdits?.investorLoanNo ?? record.investorLoanNo}
          onChange={(e) => updateField(record.servicerLoanNo, 'investorLoanNo', e.target.value)}
        />
      </TableCell>
      <TableCell align='center'>
        <TextField
          label='Type'
          value={pendingEdits?.investorLoanType ?? record.investorLoanType}
          onChange={(e) => updateField(record.servicerLoanNo, 'investorLoanType', e.target.value)}
        />
      </TableCell>
      <TableCell align='center'>
        <TextField
          label='Case Number'
          value={pendingEdits?.caseNo ?? record.caseNo}
          onChange={(e) => updateField(record.servicerLoanNo, 'caseNo', e.target.value)}
        />
      </TableCell>
      <TableCell align='center'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='First Pmnt Date'
            value={
              pendingEdits?.firstPmntDate
                ? dayjs.unix(pendingEdits.firstPmntDate)
                : record.firstPmntDate
                  ? dayjs.unix(record.firstPmntDate)
                  : null
            }
            onChange={(newValue) => {
              updateField(record.servicerLoanNo, 'firstPmntDate', newValue ? newValue.unix() : 0);
            }}
          />
        </LocalizationProvider>
      </TableCell>
      <TableCell align='center'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='Maturity Date'
            value={
              pendingEdits?.maturityDate
                ? dayjs.unix(pendingEdits.maturityDate)
                : record.maturityDate
                  ? dayjs.unix(record.maturityDate)
                  : null
            }
            onChange={(newValue) => {
              updateField(record.servicerLoanNo, 'maturityDate', newValue ? newValue.unix() : 0);
            }}
          />
        </LocalizationProvider>
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Int Rate'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.intRate ?? record.intRate}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={4}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'intRate', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Orig Prin Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.origPrinBal ?? record.origPrinBal}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'origPrinBal', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Loan FIC'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.loanFIC ?? record.loanFIC}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'loanFIC', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='LPI'
            value={pendingEdits?.lpi ? dayjs.unix(pendingEdits.lpi) : record.lpi ? dayjs.unix(record.lpi) : null}
            onChange={(newValue) => {
              updateField(record.servicerLoanNo, 'lpi', newValue ? newValue.unix() : 0);
            }}
          />
        </LocalizationProvider>
      </TableCell>
      <TableCell align='center'>
        <TextField
          label='Foreclosure'
          value={pendingEdits?.foreclosure ?? record.foreclosure}
          onChange={(e) => updateField(record.servicerLoanNo, 'foreclosure', e.target.value)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Delinq Int'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.delinqInt ?? record.delinqInt}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'delinqInt', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Delinq Prin'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.delinqPrin ?? record.delinqPrin}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'delinqPrin', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Prepaid Int'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.prepaidInt ?? record.prepaidInt}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'prepaidInt', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Prepaid Prin'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.prepaidPrin ?? record.prepaidPrin}
          customInput={TextField}
          allowNegative={false}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'prepaidPrin', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Install Int'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.installInt ?? record.installInt}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'installInt', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Install Prin'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.installPrin ?? record.installPrin}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'installPrin', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Curtailment'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.curtailment ?? record.curtailment}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'curtailment', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Adjust Int'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.adjustInt ?? record.adjustInt}
          customInput={TextField}
          allowNegative={true}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'adjustInt', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Net Adj UPB'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.netAdjustUPB ?? record.netAdjustUPB}
          customInput={TextField}
          allowNegative={true}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'netAdjustUPB', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Loan UPB'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.actualUPB ?? record.actualUPB}
          customInput={TextField}
          allowNegative={true}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'actualUPB', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='Removal Date'
            value={
              pendingEdits?.removalDate
                ? dayjs.unix(pendingEdits.removalDate)
                : record.removalDate
                  ? dayjs.unix(record.removalDate)
                  : null
            }
            onChange={(newValue) => {
              updateField(record.servicerLoanNo, 'removalDate', newValue ? newValue.unix() : 0);
            }}
          />
        </LocalizationProvider>
      </TableCell>
      <TableCell align='center'>
        <TextField
          label='Removal Reason'
          value={pendingEdits?.removalCode ?? record.removalCode}
          onChange={(e) => updateField(record.servicerLoanNo, 'removalCode', e.target.value)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Liq Int Due'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.liqIntDue ?? record.liqIntDue}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'liqIntDue', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Liq Prin Remit'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.liqPrinRemit ?? record.liqPrinRemit}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'liqPrinRemit', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Liq Prin Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.liqPrinBal ?? record.liqPrinBal}
          customInput={TextField}
          allowNegative={true}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'liqPrinBal', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='TI Bal'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.TIBal ?? record.TIBal}
          customInput={TextField}
          allowNegative={true}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'TIBal', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Sched UPB'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.schedUPB ?? record.schedUPB}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'schedUPB', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Sched Prin'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.schedMnthPrin ?? record.schedMnthPrin}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'schedMnthPrin', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Sched Int'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.schedMnthInt ?? record.schedMnthInt}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'schedMnthInt', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Serv Fee'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.grossServFee ?? record.grossServFee}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'grossServFee', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='Pmnt Date'
            value={
              pendingEdits?.pmntDate
                ? dayjs.unix(pendingEdits.pmntDate)
                : record.pmntDate
                  ? dayjs.unix(record.pmntDate)
                  : null
            }
            onChange={(newValue) => {
              updateField(record.servicerLoanNo, 'pmntDate', newValue ? newValue.unix() : 0);
            }}
          />
        </LocalizationProvider>
      </TableCell>
      <TableCell align='center'>
        <TextField
          label='Curtail Prin Code'
          value={pendingEdits?.curtailCode ?? record.curtailCode}
          onChange={(e) => updateField(record.servicerLoanNo, 'curtailCode', e.target.value)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='Arm Int Rate'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.ARMPrspIntRate ?? record.ARMPrspIntRate}
          customInput={TextField}
          allowNegative={false}
          decimalScale={4}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'ARMPrspIntRate', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          label='ARM PI'
          inputProps={{ inputMode: 'numeric' }}
          value={pendingEdits?.ARMPrspMnthPI ?? record.ARMPrspMnthPI}
          customInput={TextField}
          allowNegative={false}
          decimalScale={2}
          thousandSeparator={true}
          fixedDecimalScale={true}
          onValueChange={(values) => updateField(record.servicerLoanNo, 'ARMPrspMnthPI', values.floatValue ?? 0)}
        />
      </TableCell>
      <TableCell align='center'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='ARM Date'
            value={
              pendingEdits?.ARMAdjEffDate
                ? dayjs.unix(pendingEdits.ARMAdjEffDate)
                : record.ARMAdjEffDate
                  ? dayjs.unix(record.ARMAdjEffDate)
                  : null
            }
            onChange={(newValue) => {
              updateField(record.servicerLoanNo, 'ARMAdjEffDate', newValue ? newValue.unix() : 0);
            }}
          />
        </LocalizationProvider>
      </TableCell>
    </Fragment>
  );
});

const GNMAReportingForm = memo(() => {
  const gnmaReportingContext = useGNMAReportingContext();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState(Tables.POOLS as string);
  const tableRecords = useMemo(() => {
    return selectedTable === 'Pools' ? gnmaReportingContext.poolRecords : gnmaReportingContext.loanRecords;
  }, [gnmaReportingContext.poolRecords, gnmaReportingContext.loanRecords]);
  const [reportingDateSearch, setReportingDateSearch] = useState('');
  const [poolNumberSearch, setPoolNumberSearch] = useState('');
  const [loanNumberSearch, setLoanNumberSearch] = useState('');

  // pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalRecords = useMemo(() => tableRecords.length, [tableRecords, selectedTable]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalRecords / rowsPerPage)), [totalRecords, rowsPerPage]);
  const paginatedRecords = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return tableRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [tableRecords, page, rowsPerPage]);
  const firstDisplayedRecord = totalRecords === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const lastDisplayedRecord = Math.min(page * rowsPerPage, totalRecords);
  const isFirstPage = page === 1;
  const isLastPage = page >= totalPages;

  const handleChangeTable = (event: SelectChangeEvent) => {
    setSelectedTable(event.target.value);
    gnmaReportingContext.setPoolRecords([]);
    gnmaReportingContext.setLoanRecords([]);

    setPage(1);
  };

  const handleSearch = () => {
    selectedTable === 'Pools'
      ? gnmaReportingContext.searchPools(reportingDateSearch, poolNumberSearch)
      : gnmaReportingContext.searchLoans(reportingDateSearch, poolNumberSearch, loanNumberSearch);

    setPage(1);
  };

  const handleReset = () => {
    gnmaReportingContext.setPoolRecords([]);
    gnmaReportingContext.setLoanRecords([]);
    gnmaReportingContext.setPendingPoolEdits({});
    gnmaReportingContext.setPendingLoanEdits({});
    setReportingDateSearch('');
    setPoolNumberSearch('');
    setLoanNumberSearch('');
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setIsLoading(true);
    setRowsPerPage(Number(event.target.value));
    setPage(1);
    setIsLoading(false);
  };

  const goToFirstPage = () => {
    setIsLoading(true);
    setPage(1);
    setIsLoading(false);
  };

  const goToPreviousPage = () => {
    setIsLoading(true);
    setPage((currentPage) => Math.max(1, currentPage - 1));
    setIsLoading(false);
  };

  const goToNextPage = () => {
    setIsLoading(true);
    setPage((currentPage) => Math.min(totalPages, currentPage + 1));
    setIsLoading(false);
  };

  const goToLastPage = () => {
    setIsLoading(true);
    setPage(totalPages);
    setIsLoading(false);
  };

  return (
    <div>
      <div className={Styles.formHeader}>
        <Select value={selectedTable} label='Table Data' onChange={handleChangeTable} defaultValue={''} size='small'>
          {Object.values(Tables).map((value: string, index: number) => (
            <MenuItem key={index} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label={'Report Date (YYYYMMDD) *'}
          value={reportingDateSearch}
          onChange={(e) => setReportingDateSearch(e.target.value)}
          size='small'
        />

        {selectedTable === 'Pools' && (
          <TextField
            label={'Investor Pool(s) (Optional)'}
            value={poolNumberSearch}
            onChange={(e) => setPoolNumberSearch(e.target.value)}
            size='small'
          />
        )}

        {selectedTable === 'Loans' && (
          <Fragment>
            <TextField
              label={'Investor Pool *'}
              value={poolNumberSearch}
              onChange={(e) => setPoolNumberSearch(e.target.value)}
              size='small'
            />
            <TextField
              label={'Loan Number(s) (Optional)'}
              value={loanNumberSearch}
              onChange={(e) => setLoanNumberSearch(e.target.value)}
              size='small'
            />
          </Fragment>
        )}

        <Button variant='contained' onClick={handleSearch} disabled={isLoading}>
          Search
        </Button>
        <Button variant='contained' onClick={handleReset} disabled={isLoading}>
          Reset
        </Button>
      </div>
      {(gnmaReportingContext.loading || isLoading) && <LoadingModal open={true} />}
      <div>
        <Fragment>
          {selectedTable === 'Pools' ? (
            <Table stickyHeader aria-label='sticky label'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>
                    <Button
                      variant='contained'
                      disabled={Object.keys(gnmaReportingContext.pendingPoolEdits).length === 0}
                      onClick={() => gnmaReportingContext.saveAllPools()}
                    >
                      Save All Rows
                    </Button>
                  </TableCell>
                  <TableCell align='center'>Reporting Date</TableCell>
                  <TableCell align='center'>Pool Number</TableCell>
                  <TableCell align='center'>Adjust FIC</TableCell>
                  <TableCell align='center'>Pool FIC</TableCell>
                  <TableCell align='center'>Servicing Fee</TableCell>
                  <TableCell align='center'>WAIR</TableCell>
                  <TableCell align='center'>Net Adjust RPB</TableCell>
                  <TableCell align='center'>Def GPM Int</TableCell>
                  <TableCell align='center'>Serial Note</TableCell>
                  <TableCell align='center'>Security RPB</TableCell>
                  <TableCell align='center'>T&I Bal</TableCell>
                  <TableCell align='center'>P&I Bal</TableCell>
                  <TableCell align='center'>Other Bal</TableCell>
                  <TableCell align='center'>RR Bal</TableCell>
                  <TableCell align='center'>Const Bal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell align='center'>
                      <Button
                        variant='contained'
                        disabled={!gnmaReportingContext.pendingPoolEdits[record.poolNumber]}
                        onClick={() => gnmaReportingContext.savePoolRow(record)}
                      >
                        Save
                      </Button>
                    </TableCell>
                    <GNMAPoolRow
                      key={record.poolNumber}
                      record={record}
                      updateField={gnmaReportingContext.updatePoolField}
                      pendingEdits={gnmaReportingContext.pendingPoolEdits[record.poolNumber]}
                      // savePoolRow={gnmaReportingContext.savePoolRow}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table stickyHeader aria-label='sticky label'>
              <TableHead>
                <TableRow>
                  <Button
                    variant='contained'
                    disabled={Object.keys(gnmaReportingContext.pendingLoanEdits).length === 0}
                    onClick={() => gnmaReportingContext.saveAllLoans()}
                  >
                    Save All Rows
                  </Button>
                  <TableCell align='center'>Reporting Date</TableCell>
                  <TableCell align='center'>Pool Number</TableCell>
                  <TableCell align='center'>Servicer Loan Number</TableCell>
                  <TableCell align='center'>GNMA Loan Number</TableCell>
                  <TableCell align='center'>Type</TableCell>
                  <TableCell align='center'>Case Number</TableCell>
                  <TableCell align='center'>First Payment</TableCell>
                  <TableCell align='center'>Maturity Date</TableCell>
                  <TableCell align='center'>Interest Rate</TableCell>
                  <TableCell align='center'>Orig Prin Bal</TableCell>
                  <TableCell align='center'>Loan FIC</TableCell>
                  <TableCell align='center'>Last Paid Installment</TableCell>
                  <TableCell align='center'>Foreclosure Flag</TableCell>
                  <TableCell align='center'>Delinq Int</TableCell>
                  <TableCell align='center'>Deling Prin</TableCell>
                  <TableCell align='center'>Prepaid Int</TableCell>
                  <TableCell align='center'>Prepaid Prin</TableCell>
                  <TableCell align='center'>Install Int</TableCell>
                  <TableCell align='center'>Install Prin</TableCell>
                  <TableCell align='center'>Curtailment</TableCell>
                  <TableCell align='center'>Adjust Int</TableCell>
                  <TableCell align='center'>Net Adj UPB</TableCell>
                  <TableCell align='center'>Loan UPB</TableCell>
                  <TableCell align='center'>Removal Date</TableCell>
                  <TableCell align='center'>Removal Reason</TableCell>
                  <TableCell align='center'>Liq Int Due</TableCell>
                  <TableCell align='center'>Liq Prin Remit</TableCell>
                  <TableCell align='center'>Liq Prin Bal</TableCell>
                  <TableCell align='center'>TI Bal</TableCell>
                  <TableCell align='center'>Sched UPB</TableCell>
                  <TableCell align='center'>Sched Prin</TableCell>
                  <TableCell align='center'>Sched Int</TableCell>
                  <TableCell align='center'>Servicing Fee</TableCell>
                  <TableCell align='center'>Payment Date</TableCell>
                  <TableCell align='center'>Curtailment Prin Code</TableCell>
                  <TableCell align='center'>ARM Prosp Int Rate</TableCell>
                  <TableCell align='center'>ARM Prosp PI</TableCell>
                  <TableCell align='center'>ARM Adj Eff Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell align='center'>
                      <Button
                        variant='contained'
                        disabled={!gnmaReportingContext.pendingLoanEdits[record.servicerLoanNo]}
                        onClick={() => gnmaReportingContext.saveLoanRow(record)}
                      >
                        Save
                      </Button>
                    </TableCell>
                    <GNMALoanRow
                      key={record.servicerLoanNo}
                      record={record}
                      updateField={gnmaReportingContext.updateLoanField}
                      pendingEdits={gnmaReportingContext.pendingLoanEdits[record.servicerLoanNo]}
                      // saveRow={gnmaReportingContext.saveLoanRow}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div>
            <div>
              <label>
                Rows per page:{' '}
                <select value={rowsPerPage} onChange={handleRowsPerPageChange} aria-label='Rows per page'>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>

            <div>
              {firstDisplayedRecord}-{lastDisplayedRecord} of {totalRecords}
            </div>

            <div>
              <Tooltip placement='top' title='First Page'>
                <span>
                  <IconButton
                    onClick={goToFirstPage}
                    disabled={isFirstPage || totalRecords === 0}
                    aria-label='First page'
                    size='small'
                  >
                    <FirstPageIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement='top' title='Previous Page'>
                <span>
                  <IconButton
                    onClick={goToPreviousPage}
                    disabled={isFirstPage || totalRecords === 0}
                    aria-label='Previous page'
                    size='small'
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement='top' title='Next Page'>
                <span>
                  <IconButton
                    onClick={goToNextPage}
                    disabled={isLastPage || totalRecords === 0}
                    aria-label='Next page'
                    size='small'
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement='top' title='Last Page'>
                <span>
                  <IconButton
                    onClick={goToLastPage}
                    disabled={isLastPage || totalRecords === 0}
                    aria-label='Last page'
                    size='small'
                  >
                    <LastPageIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </Fragment>
      </div>
    </div>
  );
});

export default GNMAReportingForm;
